import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Search,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { BudgetLineItem, CostCode } from "@/hooks/use-budgets";
import { useBulkAddToBuyLog } from "@/hooks/use-budgets";
import { apiDelete } from "@/lib/api";
import { budgetKeys } from "@/lib/query";

type FilterMode = "all" | "not_on_buy_log";
type SortBy = "code" | "description" | "division";
type SortDir = "asc" | "desc";

interface CostCodeBrowserProps {
  projectId: string;
  costCodes: CostCode[];
  lineItems: BudgetLineItem[];
  canEdit: boolean;
  isVisible: boolean;
}

function normalize(s: string): string {
  return s.replace(/\s+/g, "").replace(/^0+/, "").toLowerCase();
}

function highlightMatch(text: string, query: string): ReactNode {
  if (!query) return text;
  const lower = text.toLowerCase();
  const lowerQ = query.toLowerCase();
  const idx = lower.indexOf(lowerQ);
  if (idx === -1) {
    const normText = normalize(text);
    const normQ = normalize(query);
    const nIdx = normText.indexOf(normQ);
    if (nIdx === -1) return text;
    return <mark className="bg-yellow-200 rounded-sm">{text}</mark>;
  }
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 rounded-sm">{text.slice(idx, idx + lowerQ.length)}</mark>
      {text.slice(idx + lowerQ.length)}
    </>
  );
}

export function CostCodeBrowser({
  projectId,
  costCodes,
  lineItems,
  canEdit,
  isVisible,
}: CostCodeBrowserProps) {
  const qc = useQueryClient();
  const searchRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const [divisionPopoverOpen, setDivisionPopoverOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>("code");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);

  const addMutation = useBulkAddToBuyLog(projectId);

  useEffect(() => {
    if (isVisible) {
      searchRef.current?.focus();
    }
  }, [isVisible]);

  const onBuyLogSet = useMemo(() => new Set(lineItems.map((l) => l.costCodeId)), [lineItems]);

  const unbudgetedSet = useMemo(
    () =>
      new Set(
        lineItems
          .filter((l) => !l.originalBudgetedAmt || Number(l.originalBudgetedAmt) === 0)
          .map((l) => l.costCodeId),
      ),
    [lineItems],
  );

  const csiDivisions = useMemo(() => {
    const divs = new Set<string>();
    for (const c of costCodes) {
      if (c.csiDivision) divs.add(c.csiDivision);
    }
    return [...divs].sort();
  }, [costCodes]);

  const filteredCodes = useMemo(() => {
    let list = costCodes;

    if (searchQuery.trim()) {
      const q = searchQuery.trim();
      const normQ = normalize(q);
      const lowerQ = q.toLowerCase();
      list = list.filter((c) => {
        if (normalize(c.code).includes(normQ)) return true;
        if (c.description?.toLowerCase().includes(lowerQ)) return true;
        if (c.csiDivision?.toLowerCase().includes(lowerQ)) return true;
        if (c.csiSubdivision?.toLowerCase().includes(lowerQ)) return true;
        return false;
      });
    }

    if (filterMode === "not_on_buy_log") {
      list = list.filter((c) => !onBuyLogSet.has(c.id));
    }

    if (selectedDivisions.length > 0) {
      list = list.filter((c) => c.csiDivision && selectedDivisions.includes(c.csiDivision));
    }

    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortBy === "code") cmp = a.code.localeCompare(b.code);
      else if (sortBy === "description")
        cmp = (a.description ?? "").localeCompare(b.description ?? "");
      else if (sortBy === "division")
        cmp = (a.csiDivision ?? "").localeCompare(b.csiDivision ?? "");
      return sortDir === "asc" ? cmp : -cmp;
    });

    return list;
  }, [costCodes, searchQuery, filterMode, selectedDivisions, sortBy, sortDir, onBuyLogSet]);

  const allVisibleSelectable = filteredCodes.filter((c) => !onBuyLogSet.has(c.id));
  const allVisibleSelected =
    allVisibleSelectable.length > 0 && allVisibleSelectable.every((c) => selectedIds.has(c.id));
  const someVisibleSelected = allVisibleSelectable.some((c) => selectedIds.has(c.id));

  function handleSelectAll(checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const c of allVisibleSelectable) {
        checked ? next.add(c.id) : next.delete(c.id);
      }
      return next;
    });
  }

  function handleToggleRow(id: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  }

  function handleSortClick(col: SortBy) {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  }

  function SortIcon({ col }: { col: SortBy }) {
    if (sortBy !== col) return <ChevronsUpDown className="ml-1 h-3.5 w-3.5 text-gray-400" />;
    return sortDir === "asc" ? (
      <ChevronUp className="ml-1 h-3.5 w-3.5" />
    ) : (
      <ChevronDown className="ml-1 h-3.5 w-3.5" />
    );
  }

  function handleAddClick() {
    if (selectedIds.size === 0) return;
    if (selectedIds.size > 50) {
      setConfirmBulkOpen(true);
    } else {
      executeBulkAdd();
    }
  }

  function executeBulkAdd() {
    const selectedCodes = costCodes.filter((c) => selectedIds.has(c.id));
    const payloads = selectedCodes.map((c, i) => ({
      costCodeId: c.id,
      description: c.description ?? c.code,
      csiDivision: c.csiDivision,
      originalBudgetedAmt: "0",
      sortOrder: lineItems.length + i,
    }));

    addMutation.mutate(
      { lineItems: payloads },
      {
        onSuccess: (result) => {
          const count = selectedCodes.length;
          const unbudgetedCount = selectedCodes.filter((c) => !onBuyLogSet.has(c.id)).length;
          const newIds = result.data.map((l) => l.id);

          setSelectedIds(new Set());

          toast.success(`${count} cost code${count === 1 ? "" : "s"} added to Buy Log`, {
            duration: 10_000,
            action: {
              label: "Undo",
              onClick: () => handleUndo(newIds),
            },
          });

          if (unbudgetedCount > 0) {
            toast.info(
              `${unbudgetedCount} of ${count} code${unbudgetedCount === 1 ? "" : "s"} ${unbudgetedCount === 1 ? "is" : "are"} not on the project budget — flagged for review`,
            );
          }
        },
      },
    );
  }

  async function handleUndo(lineIds: string[]) {
    try {
      await Promise.all(
        lineIds.map((id) => apiDelete(`/projects/${projectId}/budget/line-items/${id}`)),
      );
      qc.invalidateQueries({ queryKey: budgetKeys.project(projectId) });
      toast.success("Undo successful");
    } catch {
      toast.error("Undo failed — some lines may not have been removed");
    }
  }

  function toggleDivision(div: string) {
    setSelectedDivisions((prev) =>
      prev.includes(div) ? prev.filter((d) => d !== div) : [...prev, div],
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Search + count */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by code, description, or CSI division…"
            className="h-9 w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-9 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
          {searchQuery ? (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
        <span className="shrink-0 text-sm text-gray-500">
          Showing{" "}
          <span className="font-medium text-gray-900">{filteredCodes.length.toLocaleString()}</span>{" "}
          of <span className="font-medium text-gray-900">{costCodes.length.toLocaleString()}</span>{" "}
          cost codes
        </span>
      </div>

      {/* Filter controls */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Filter chips */}
        <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm">
          <button
            type="button"
            onClick={() => setFilterMode("all")}
            className={`rounded-md px-3 py-1.5 text-sm ${
              filterMode === "all" ? "bg-gray-900 text-white" : "text-gray-600 hover:text-gray-900"
            }`}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setFilterMode("not_on_buy_log")}
            className={`rounded-md px-3 py-1.5 text-sm ${
              filterMode === "not_on_buy_log"
                ? "bg-gray-900 text-white"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Not Yet on Buy Log
          </button>
        </div>

        {/* CSI Division filter */}
        <Popover open={divisionPopoverOpen} onOpenChange={setDivisionPopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 gap-1.5">
              CSI Division
              {selectedDivisions.length > 0 ? (
                <Badge variant="secondary" className="ml-1 rounded-full px-1.5 py-0 text-xs">
                  {selectedDivisions.length}
                </Badge>
              ) : null}
              <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2" align="start">
            <div className="max-h-60 overflow-y-auto space-y-1">
              {csiDivisions.map((div) => (
                <button
                  key={div}
                  type="button"
                  onClick={() => toggleDivision(div)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-gray-50"
                >
                  <div
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      selectedDivisions.includes(div)
                        ? "border-gray-900 bg-gray-900 text-white"
                        : "border-gray-300"
                    }`}
                  >
                    {selectedDivisions.includes(div) ? <Check className="h-3 w-3" /> : null}
                  </div>
                  <span className="truncate">{div}</span>
                </button>
              ))}
            </div>
            {selectedDivisions.length > 0 ? (
              <div className="mt-2 border-t pt-2">
                <button
                  type="button"
                  onClick={() => setSelectedDivisions([])}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear filter
                </button>
              </div>
            ) : null}
          </PopoverContent>
        </Popover>

        {/* Selection controls */}
        {selectedIds.size > 0 ? (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-gray-600">
              <span className="font-semibold text-gray-900">{selectedIds.size}</span> selected
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
              className="h-7 px-2 text-xs text-gray-500"
            >
              Clear selection
            </Button>
          </div>
        ) : null}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-10 pl-4">
                {allVisibleSelectable.length > 0 ? (
                  <Checkbox
                    checked={
                      allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false
                    }
                    onCheckedChange={(v) => handleSelectAll(!!v)}
                    aria-label="Select all visible"
                  />
                ) : null}
              </TableHead>
              <TableHead
                className="w-32 cursor-pointer select-none whitespace-nowrap"
                onClick={() => handleSortClick("code")}
              >
                <span className="inline-flex items-center">
                  Cost Code <SortIcon col="code" />
                </span>
              </TableHead>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => handleSortClick("description")}
              >
                <span className="inline-flex items-center">
                  Description <SortIcon col="description" />
                </span>
              </TableHead>
              <TableHead
                className="w-56 cursor-pointer select-none whitespace-nowrap"
                onClick={() => handleSortClick("division")}
              >
                <span className="inline-flex items-center">
                  CSI Division <SortIcon col="division" />
                </span>
              </TableHead>
              <TableHead className="w-28 text-right">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCodes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-12 text-center text-sm text-gray-500">
                  No cost codes match your search.
                </TableCell>
              </TableRow>
            ) : (
              filteredCodes.map((code) => {
                const isOnBuyLog = onBuyLogSet.has(code.id);
                const isUnbudgeted = unbudgetedSet.has(code.id);
                const isSelected = selectedIds.has(code.id);
                const q = searchQuery.trim();
                return (
                  <TableRow
                    key={code.id}
                    className={`${isSelected ? "bg-blue-50" : ""} ${isOnBuyLog ? "opacity-70" : ""}`}
                  >
                    <TableCell className="pl-4">
                      <Checkbox
                        checked={isSelected}
                        disabled={!canEdit || isOnBuyLog}
                        onCheckedChange={(v) => handleToggleRow(code.id, !!v)}
                        aria-label={`Select ${code.code}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {highlightMatch(code.code, q)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {code.description ? (
                        highlightMatch(code.description, q)
                      ) : (
                        <span className="text-gray-400 italic">No description</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {code.csiDivision ? highlightMatch(code.csiDivision, q) : null}
                      {code.csiSubdivision ? (
                        <span className="ml-1 text-gray-400">· {code.csiSubdivision}</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center justify-end gap-1.5">
                        {isUnbudgeted ? (
                          <span title="No budget amount allocated" className="inline-flex">
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                          </span>
                        ) : null}
                        {isOnBuyLog ? (
                          <Badge
                            variant="secondary"
                            className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                          >
                            On Buy Log
                          </Badge>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Sticky add bar */}
      {canEdit ? (
        <div className="sticky bottom-4 flex items-center justify-end">
          <Button
            onClick={handleAddClick}
            disabled={selectedIds.size === 0 || addMutation.isPending}
            className="shadow-lg"
          >
            {addMutation.isPending
              ? "Adding…"
              : selectedIds.size === 0
                ? "Add Cost Codes to Buy Log"
                : `Add ${selectedIds.size} Cost Code${selectedIds.size === 1 ? "" : "s"} to Buy Log`}
          </Button>
        </div>
      ) : null}

      {/* Bulk confirmation dialog (N > 50) */}
      <AlertDialog open={confirmBulkOpen} onOpenChange={setConfirmBulkOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add {selectedIds.size} cost codes?</AlertDialogTitle>
            <AlertDialogDescription>
              You're about to add {selectedIds.size} cost codes to this project's Buy Log at once.
              This will create {selectedIds.size} new line items. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmBulkOpen(false);
                executeBulkAdd();
              }}
            >
              Add {selectedIds.size} Cost Codes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
